import { ITechApi } from '@kollective-frontend/ksdk-api';
import { TechApi, getValueFromTech } from '../bootloader/tech';
import ContentConfig from '../config/ContentConfig';
import PlayerElement from '../util/PlayerElement';
import {
  EngagementEvents,
  EventObject,
  MonitoredMediaEvents,
  StatefulPlayerEvents,
  TechEngagementMethods
} from '../common/Reporter/constants';
import transformDuration from '../common/Reporter/transformDuration';
import {
  BasePlayerEvent,
  TrackInfo,
  VisibilityState
} from '../reporter/constants';
import logger from '../log';
const log = logger.getLogger(__filename);

const { pause, play, seeking } = MonitoredMediaEvents;
const { AUDIOCHANGED, BITRATECHANGED, CAPTIONCHANGED } = TechEngagementMethods;

/**
 * Gets the current visibility state of the browser tab
 *
 * @returns O - if hidden, 1 - if visible
 */
const getVisibilityState = (): VisibilityState => {
  const { VISIBLE, HIDDEN } = VisibilityState;
  return document.visibilityState === 'visible' ? VISIBLE : HIDDEN;
};

/**
 * NOTE: Exported for unit tests
 */
export const isEligibleStopEvent = (evt: Event): boolean => {
  switch (evt.type) {
    case 'beforeunload':
      return true;
    case 'pagehide':
      return !(evt as PageTransitionEvent).persisted;
    default:
      return false;
  }
};

function handleStopEvent(e: number, evt: Event): BasePlayerEvent | undefined {
  if (isEligibleStopEvent(evt)) {
    return { e };
  }
}

/**
 * Determine if the window is in PiP state
 *
 * NOTE: document.pictureInPictureElement is the element that is in PIP mode,
 * if it is the same as our element, then we are in PIP mode.
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the code for the BasePlayerEvent
 * @param evt - the PIP event or other event
 * @param element - the media element
 * @returns BasePlayerEvent if PIP is supported
 */
export function getPipState(
  e: number,
  evt: Event,
  element?: PlayerElement
): BasePlayerEvent | undefined {
  // Firefox doesn't support PiP.
  if (typeof window.PictureInPictureEvent === 'undefined') {
    return;
  }
  if (evt instanceof PictureInPictureEvent) {
    const pipActive = evt.type === 'enterpictureinpicture';
    const { target } = evt;
    return { e, pip: pipActive ? target === element : false };
  }
  return { e, pip: element === document.pictureInPictureElement };
}

/**
 * Get a muted event. (NOTE: this will not return an event for Fetch or XHR
 * factory integrations unless the 'video' they send us has a muted field).
 *
 * @param e - the code for the event
 * @param element - the HTMLMediaElement.
 * @returns an event if the element has a `muted` field, otherwise undefined
 */
export function getMaybeMutedEvent(
  e: number,
  element: HTMLMediaElement
): BasePlayerEvent | undefined {
  // Because some integrations (MS) do something weird with the volume, we need
  // to check if the volume is 0 or if it's muted.
  if (typeof element?.muted === 'undefined') {
    if (typeof element?.volume !== 'undefined') {
      return { e, mute: element.volume === 0 };
    }
    return;
  }
  return { e, mute: element?.muted || element?.volume === 0 };
}

/**
 * Check to see if this window is fullscreen (we can't tell if the video is
 * fullscreen but we can tell if this browser tab is).
 */
function isFullscreen() {
  return !!document.fullscreenElement;
}

type BPEorUndef = BasePlayerEvent | undefined;

//#region Bitrate, TrackInfo or Resolution
/**
 * check that a field is a number and is not 0.
 *
 * @param val - number to check
 */
const checkNumField = (val?: number) => typeof val === 'number' && val !== 0;

/**
 * Utility event to check if a video resolution event is valid
 *
 * @param height - height of the resolution
 * @param width - width of the resolution
 * @param evt - event to pass through
 * @param from - function this is from
 */
export function resEvent(
  height: number,
  width: number,
  evt: BasePlayerEvent,
  from: string
): BasePlayerEvent | undefined {
  if (checkNumField(height) && checkNumField(width)) {
    log.trace(`${from}:`, evt);
    return evt;
  }
}

/**
 * Get the client (video element) resolution player event
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the event code
 * @param element -- the PlayerElement
 * @param event -- the event triggering this
 * @returns BasePlayerEvent | undefined
 */
export function getClientVideoResolutionEvent(
  e: number,
  event: Event,
  element: PlayerElement
): BasePlayerEvent | undefined {
  const ele = event.target ?? element;
  const { clientHeight: height, clientWidth: width } = ele as HTMLVideoElement;
  const evt = { e, cr: `${width}x${height}` };
  return resEvent(height, width, evt, 'getClientVideoResolutionEvent');
}

/**
 * Get the video resolution player event
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the event code
 * @param event -- the event triggering this
 * @param element -- the PlayerElement
 * @returns BasePlayerEvent | undefined
 */
export function getVideoResolution(
  e: number,
  event: Event,
  element: PlayerElement
): BasePlayerEvent | undefined {
  const ele = event.target ?? element;
  const { videoHeight: height, videoWidth: width } = ele as HTMLVideoElement;
  const evt = { e, vr: `${width}x${height}` };
  return resEvent(height, width, evt, 'getVideoResolution');
}

/**
 * Check to see if an object is a track info object.
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the event code
 * @param tech - the tech object to get the bitrate from
 * @returns BasePlayerEvent | undefined
 */
export function isTrackInfo(obj: unknown): obj is TrackInfo {
  const i = obj as TrackInfo;
  return typeof i?.lang === 'string' && typeof i?.name === 'string';
}

/**
 * Get the current caption track info event, or undefined if unable to
 * determine which track we're using
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the event code
 * @param tech - the tech object to get the bitrate from
 * @returns BasePlayerEvent | undefined
 */
export function getCaptionTrackInfo(e: number, tech?: TechApi): BPEorUndef {
  const ct = getValueFromTech(tech, CAPTIONCHANGED);
  log.trace('getCaptionTrackInfo:', ct);
  return isTrackInfo(ct) ? { e, ct } : undefined;
}

/**
 * Get the audio track information event, or undefined if unable to
 * determine which track we're using
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the event code
 * @param tech - the tech object to get the bitrate from
 * @returns BasePlayerEvent | undefined
 */
export function getAudioTrackInfo(e: number, tech?: TechApi): BPEorUndef {
  const at = getValueFromTech(tech, AUDIOCHANGED);
  log.trace('getAudioTrackInfo:', at);
  return isTrackInfo(at) ? { e, at } : undefined;
}

/**
 * Get the bitrate event, or undefined if the bitrate is empty or 0.
 *
 * NOTE: That the bitrate event does not end up in the events in ends up in
 * the bitrates section of the report.
 *
 * INFO: exported for testing, not expected to be used elsewhere
 *
 * @param e - the event code
 * @param tech - the tech object to get the bitrate from
 * @returns BasePlayerEvent | undefined
 */
export function getBitratePlayerEvent(
  e: number,
  tech?: ITechApi
): BasePlayerEvent | undefined {
  const b = getValueFromTech(tech, BITRATECHANGED);
  return typeof b === 'number' && b !== 0 ? { b, e } : undefined;
}
//#endregion Bitrate, TrackInfo or Resolution

/**
 * When we start up, get a new session token, or other possible cases;
 * This gives a list of all the appropriate events to set up the initial
 * state for the analytics.
 *
 * @param element - the element that most metadata can be gathered from
 * @param tech - the tech object to get "global" events from
 * @returns a list of player events, some might be missing based on the
 *          state of the player and tech (for example, if the player/tech
 *          doesn't have a metadata field then some events will return
 *          undefined.
 */
export function getStatusEvents(
  element: PlayerElement,
  tech: TechApi
): BasePlayerEvent[] {
  if (shouldSkipEngagementEvent()) {
    return [];
  }
  const events: BasePlayerEvent[] = [];
  const i = new Event('ignored');
  // We only want one of seeking, paused, or play events:
  // NOTE: XHR and Fetch factory integrations will always show as play, unless
  // they send us a real HTMLMediaElement or one with `seeking` and `paused`
  // defined.
  if (element?.seeking) {
    pushIf(events, getEventForEventMetadata(seeking, i, element, tech));
  } else if (element?.paused) {
    pushIf(events, getEventForEventMetadata(pause, i, element, tech));
  } else {
    pushIf(events, getEventForEventMetadata(play, i, element, tech));
  }
  // We want all available stateful events.
  StatefulPlayerEvents.forEach(spEvent => {
    pushIf(events, getEventForEventMetadata(spEvent, i, element, tech));
  });
  log.debug('getStatusEvents:', events);
  events.forEach(e => (e.s = true));
  return events;
}

/**
 * Push an item onto a list if it is defined and not null.
 *
 * @param list - list of items
 * @param item - item to add if defined.
 */
function pushIf<T>(list: T[], item?: T) {
  if (typeof item != 'undefined' && item !== null) {
    list.push(item);
  }
}

/**
 * Checks if undefined should be returned rather than get the player event
 * based on the state of config.report.enableEngagementEvent, and if a code
 * was passed in that's an EngagementEvent.
 *
 * @param e - event code or undefined
 * @returns false if code passed in is "undefined" or in the EngagementEvents
 * list, and the enableEngagementEvent is true. Otherwise returns false
 */
export function shouldSkipEngagementEvent(e?: number): boolean {
  e = e ?? EngagementEvents[0];
  const enabled = ContentConfig.getInstance().report.enableEngagementEvent;
  return EngagementEvents.indexOf(e) !== -1 && !enabled;
}

/**
 * Simplified interface to handle any event and create the appropriate
 * player event for the reporting.
 *
 * @param eo - the event name, code, metadata
 * @param event - a javascript event, used for some of the handlers
 * @param element - the media element or it's substitute
 * @param tech - the object containing functionality for Non HTML events
 * @returns the player event if available
 */
function getEventForEventMetadata(
  eo: EventObject,
  event: Event,
  element: PlayerElement,
  tech: TechApi
): BasePlayerEvent | undefined {
  const { metadata, code: e } = eo;
  if (shouldSkipEngagementEvent(e)) {
    return;
  }
  switch (metadata) {
    // Media/Video Events
    case 'dur':
      return { e, dur: transformDuration(element?.duration) };
    case 'mute':
      return getMaybeMutedEvent(e, element as HTMLMediaElement);
    case 'pos':
      return { e, pos: element?.currentTime };
    case 'rate':
      return { e, rate: element?.playbackRate };
    case 'vr':
      return getVideoResolution(e, event, element);
    // HTMLElement Custom Events
    case 'cr':
      return getClientVideoResolutionEvent(e, event, element);
    // Player events
    case 'audio':
      return getAudioTrackInfo(e, tech);
    case 'b':
      return getBitratePlayerEvent(e, tech);
    case 'caption':
      return getCaptionTrackInfo(e, tech);
    // Window/Document Events
    case 'foc':
      return { e, foc: document.hasFocus() };
    case 'full':
      return { e, full: isFullscreen() };
    case 'pip':
      // Should always be called with tech available.
      return getPipState(e, event, element);
    case 'vis':
      return { e, vis: getVisibilityState() };
    case 'stop':
      return handleStopEvent(e, event);
    default:
      return { e };
  }
}

export default getEventForEventMetadata;
